import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

import { RootState } from '../../../../../core/state/state';
import { BookingModalType } from '../../../../../core/state/bookings/types';
import { NotificationPosition } from '../../../../../core/state/global-notifications/types';
import { Color } from '../../../../../core/enums/Color';
import { getExternalUserContacts } from '../../../../../core/state/subcontractors/asyncActions';
import { selectProjectSettings } from '../../../../../core/state/bookingProjectSettings/selectors';
import { contactsToOptions, equipmentToOption, unloadingTimeToOptions } from '../../../helpers';
import Input from '../../../../../components/form/input/Input';
import TextArea from '../../../../../components/form/text-area/TextArea';
import Select from '../../../../../components/form/select/Select';
import AddCompanyContact from '../../../../company-contacts/modals/add-company-contact/AddCompanyContactModal';
import Button from '../../../../../components/form/button/Button';
import Icon from '../../../../../components/icon/Icon';
import { isMobileScreenSize } from '../../../../../core/utils/isMobileScreenSize';

import './CreateExternalStepOne.scss';

interface Props {
  control: Control<any>;
  errorInputName: string | null;
  setValue: UseFormSetValue<any>;
}

const CreateExternalStepOne: React.FC<Props> = ({ control, errorInputName, setValue }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobileScreen = isMobileScreenSize();

  const { isOpen: isAnyModalOpen, data } = useSelector((state: RootState) => state.bookings.modal);
  const isOpen = isAnyModalOpen && data?.type === BookingModalType.External;
  const [isAddCompanyContactModalOpen, setIsAddCompanyContactModalOpen] = useState<boolean>(false);
  const contacts = useSelector((state: RootState) => state.subcontractor.contacts);
  const userID = useSelector((state: RootState) => state.user.userInfo?.userID);
  const projectSettings = useSelector(selectProjectSettings);
  const {
    bookingProjectEquipment: transportEquipment,
    bookingProjectSettings: { deliveryDurationMax, deliveryDurationMin }
  } = projectSettings;
  const userName = useSelector((state: RootState) => state.auth.authInfo?.account.name ?? '');

  const equipmentOptions = useMemo(() => transportEquipment?.map(equipmentToOption), [transportEquipment]);
  const unloadingTimeOptions = useMemo(
    () => unloadingTimeToOptions(deliveryDurationMin, deliveryDurationMax),
    [deliveryDurationMax, deliveryDurationMin]
  );
  const contactOptions = useMemo(() => contacts?.map(contactsToOptions), [contacts]);

  const handleContact = (id: string) => {
    setValue('contactPersonId', id);
  };

  useEffect(() => {
    if (userID) {
      dispatch(getExternalUserContacts(userID));
    }
  }, [dispatch, userID]);

  if (!isOpen) {
    return <></>;
  }

  return (
    <form className="create-booking-modal">
      <div className={`create-booking-modal__content ${isMobileScreen ? 'create-booking-modal__content--mobile' : ''}`}>
        <label className="input__label" htmlFor="additional-title">
          {t('booking.title')}
        </label>
        <p className="input__label create-booking-modal__title">{userName}</p>
        <div className={`${!isMobileScreen && 'form-row'}`}>
          <Controller
            name="date"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                label={t('booking.deliveryDate')}
                id="date"
                name={name}
                type="date"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                required
                error={errorInputName === 'date' ? 'error' : undefined}
                errorTextVisible={false}
              />
            )}
          />
          <Controller
            name="deliveryDuration"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Select
                id="delivery-duration"
                name={name}
                className="create-booking-modal__delivery-duration"
                showSearch={false}
                label={t('booking.deliveryDuration')}
                value={value}
                required
                onChange={(option) => {
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  onChange(option.value);
                }}
                options={unloadingTimeOptions}
                addPlaceholderOption
              />
            )}
          />
        </div>
        <Controller
          name="contactPersonId"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <Select
              id="contact-person"
              name={name}
              label={t('booking.contactPersonSubcontractor')}
              showSearch={false}
              value={String(value)}
              required
              onChange={(option) => {
                if (Array.isArray(option)) {
                  [option] = option; // eslint-disable-line no-param-reassign
                }
                onChange(option.value);
              }}
              options={contactOptions}
              addPlaceholderOption
            />
          )}
        />
        <Button
          color={Color.Blue}
          buttonTypes={['transparent']}
          onClick={() => setIsAddCompanyContactModalOpen(true)}
          className="create-external-step-one"
        >
          <Icon className="create-external-step-one__icon-plus" icon="plus" size="extra-small" svg />
          <span>{t('common.addNew')}</span>
        </Button>

        <div className="form-row">
          <Controller
            name="transportEquipmentId"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Select
                id="transport-equipment"
                label={t('booking.transport')}
                value={value}
                showSearch={false}
                name={name}
                options={equipmentOptions}
                onChange={(option) => {
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  onChange(option.value);
                }}
                addPlaceholderOption
                required
              />
            )}
          />
          <span />
        </div>
        <Controller
          name="description"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <TextArea
              label={t('booking.description')}
              placeholder={t('common.inputPlaceholder')}
              id="delivery-item"
              name={name}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              required
              maxLength={500}
            />
          )}
        />
        {isAddCompanyContactModalOpen && (
          <AddCompanyContact
            isMobile={isMobileScreen}
            isOpen={isAddCompanyContactModalOpen}
            closeModal={() => setIsAddCompanyContactModalOpen(false)}
            notificationSuccessTitle={t('company.modal.contactAdded')}
            notificationSuccessMessage={t('company.modal.form.message.success')}
            notificationPosition={NotificationPosition.Top}
            onHandleContact={handleContact}
          />
        )}
      </div>
    </form>
  );
};

export default CreateExternalStepOne;
